import API from "../../Api";
import {
  DeliveryCharge,
  DeliveryChargeRequestBody,
  getDeliveryDetails,
} from "./DeliveryModels";
import { encryptJson } from "../..//utils/react-ec-utils";
import { createRequest } from "../../utils/hmac-utils";

export async function getDeliveryChargeAPI(payload: DeliveryChargeRequestBody) {
  const normalizedDeliveryProviderId = String(payload.deliveryProviderId ?? "")
    .trim()
    .replace(/^'+|'+$/g, "");

  const requestPayload: DeliveryCharge = {
    pickupDetails: payload.pickupDetails,
    dropDetails: payload.dropDetails,
    locationId: payload.locationId,
    deliveryProviderId: normalizedDeliveryProviderId,
    receiverDetails:payload.receiverDetails,
    senderDetails: payload.senderDetails,
    customerId: payload.customerId ?? "",
    addressId: payload.addressId ?? "",
    tip: Math.floor(payload.tip),
    emailId: payload.emailId ?? "",
    mobileNo: payload.mobileNo ?? "",
  };
  
  // const data = encryptJson(requestPayload);
  const ecnLocationProviderId = encodeURIComponent(encryptJson(normalizedDeliveryProviderId));
   const hmacRequest = await createRequest(requestPayload);

  return API({
    method: "post",
    url: `/api/customers/delivery/quote?deliveryProviderId=${ecnLocationProviderId}`,
    // data:{"data":hmacRequest}
    data : hmacRequest
  });
}

export async function dunzoOrderTrackingAPI(payload: getDeliveryDetails) {
  const data = {
    deliveryProviderId: payload?.DeliveryProviders,
    orderId: payload?.orderId,
    emailId: payload?.emailId ?? "",
    mobileNo: payload?.mobileNo ?? "",
  };
  // const payloadData = encryptJson(data);
   const hmacRequest = await createRequest(data);
  return API({
    method: "post",
    url: `/api/customers/delivery/status/v2`,
    // data: {"data": hmacRequest}
        data : hmacRequest
  });
}

// Instore Valet app tracking
export function getValetLocation(deliveryStaffId: string) {
  return API({
    method: "get",
    url: `/valet/getValetLocation?deliveryStaffId=${deliveryStaffId}`,
  });
}

/**
 * maghil-backend-v3, which serves the Burq delivery quote.
 *
 * The same variable the Stripe calls use. Despite its name,
 * `REACT_APP_API_ENDPOINT_V3` points at a different service that does not have
 * these routes.
 */
const V3_BASE = process.env.REACT_APP_PAYMENTS_API_ENDPOINT ?? "";

/**
 * Quotes a delivery fee for a location on Burq.
 *
 * Deliberately not the gateway's `/api/customers/delivery/quote`. That one asks
 * the courier what it will charge; Burq cannot answer that synchronously — its
 * quoting is asynchronous and arrives minutes later by webhook, long after the
 * diner has chosen an address. So a Burq location is priced from its own
 * distance bands in `mh_attribute` (`entity_type = 'DELIVERY_FEE'`), which v3
 * computes from the driving distance between the two points.
 *
 * That means the diner is quoted the restaurant's published price rather than
 * the courier's cost. The difference between the two is the restaurant's to
 * absorb or keep, which is the same arrangement every band-priced delivery has.
 *
 * Plain JSON, no HMAC envelope: v3 does not sign its responses, so the caller
 * reads `response.data` directly instead of going through
 * `validateAndParseResponse`.
 */
export async function getBurqDeliveryQuoteAPI(payload: DeliveryChargeRequestBody) {
  const base = V3_BASE.replace(/\/+$/, "");

  const response = await fetch(`${base}/api/v1/delivery/quote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      locationId: payload.locationId,
      pickup: { lat: payload.pickupDetails?.lat, lng: payload.pickupDetails?.lng },
      delivery: { lat: payload.dropDetails?.lat, lng: payload.dropDetails?.lng },
    }),
  });

  // 400 means the location has no bands configured, 502 that the distance
  // could not be measured. Both are read by the caller from `ok`, because the
  // body still carries a message worth showing.
  const data = await response.json().catch(() => null);
  return { ok: response.ok, status: response.status, data };
}

/**
 * The current state of a Burq delivery, from maghil-backend-v3.
 *
 * Not the gateway's tracking endpoint. That one resolves the courier through
 * the Java factory, which does not know `BURQ_PROVIDER` — so for a Burq order
 * it throws, is caught, and returns nothing. The tracking link then never
 * renders even though the URL is stored.
 *
 * v3 answers from the delivery row, which Burq keeps current by webhook:
 * status, fee and tracking URL all arrive that way, so the row is the freshest
 * source there is.
 *
 * 404 is an ordinary answer — a pickup order, or a delivery not dispatched yet.
 */
export async function getBurqDeliveryStatusAPI(orderId: string) {
  const base = V3_BASE.replace(/\/+$/, "");

  const response = await fetch(
    `${base}/api/v1/delivery/status/${encodeURIComponent(orderId)}`,
  );

  const data = await response.json().catch(() => null);
  return { ok: response.ok, status: response.status, data };
}
